import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  EyeIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  BanknotesIcon,
  XCircleIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import StatsCard from '../components/UI/StatsCard';
import WithdrawalDetailModal from '../components/withdrawals/WithdrawalDetailModal';
import AddWithdrawalModal from '../components/withdrawals/AddWithdrawalModal';
import withdrawalService, { WithdrawalListItem } from '../api/services/withdrawal.service';

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    pending: 'bg-yellow-50 text-yellow-700',
    accepted: 'bg-blue-50 text-blue-700',
    paid: 'bg-green-50 text-green-700',
    rejected: 'bg-red-50 text-red-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const Withdraw: React.FC = () => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'pending' | 'accepted' | 'paid' | 'rejected'
  >('all');

  const [detailId, setDetailId] = useState<string | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);

  /* =========================================================
     STATS QUERY
     - 5m staleTime
     - lazy: refetches only after a mutation or when the cache
       goes stale AND the component remounts
  ========================================================= */
  const { data: stats } = useQuery({
    queryKey: ['withdrawal-stats'],
    queryFn: () => withdrawalService.getStats(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  /* =========================================================
     LIST QUERY
     - Key captures every filter so paging caches independently
  ========================================================= */
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['withdrawals', page, limit, searchTerm, statusFilter],
    queryFn: () =>
      withdrawalService.getAll({
        page,
        limit,
        search: searchTerm || undefined,
        status: statusFilter,
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const list = data?.withdrawals || [];
  const pagination = data?.pagination || { page: 1, limit: 10, total: 0, pages: 0 };

  const handleSearchSubmit = () => {
    setSearchTerm(localSearch);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearchSubmit();
  };

  const handleClearFilters = () => {
    setLocalSearch('');
    setSearchTerm('');
    setStatusFilter('all');
    setPage(1);
  };

  /* =========================================================
     MUTATION SUCCESS — the list and stats are what the admin
     is looking at, so refetch them eagerly. Dashboard/users
     caches are marked stale but not refetched right now.
  ========================================================= */
  const handleChanged = () => {
    queryClient.refetchQueries({ queryKey: ['withdrawals'] });
    queryClient.refetchQueries({ queryKey: ['withdrawal-stats'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const s = stats || {
    total: 0, totalAmount: 0, today: 0, todayAmount: 0,
    pending: 0, pendingAmount: 0,
    accepted: 0, acceptedAmount: 0,
    paid: 0, paidAmount: 0,
    rejected: 0, rejectedAmount: 0,
  };

  const statCards = [
    {
      title: 'Total Requests',
      value: s.total.toLocaleString(),
      todayValue: s.today.toLocaleString(),
      icon: <WalletIcon className="w-5 h-5" />,
      color: 'primary' as const,
      showToday: true,
    },
    {
      title: 'Pending',
      value: s.pending.toLocaleString(),
      todayValue: `ETB ${s.pendingAmount.toLocaleString()}`,
      icon: <ClockIcon className="w-5 h-5" />,
      color: 'warning' as const,
      showToday: false,
    },
    {
      title: 'Accepted',
      value: s.accepted.toLocaleString(),
      todayValue: `ETB ${s.acceptedAmount.toLocaleString()}`,
      icon: <CheckCircleIcon className="w-5 h-5" />,
      color: 'secondary' as const,
      showToday: false,
    },
    {
      title: 'Paid',
      value: s.paid.toLocaleString(),
      todayValue: `ETB ${s.paidAmount.toLocaleString()}`,
      icon: <BanknotesIcon className="w-5 h-5" />,
      color: 'success' as const,
      showToday: false,
    },
    {
      title: 'Rejected',
      value: s.rejected.toLocaleString(),
      todayValue: `ETB ${s.rejectedAmount.toLocaleString()}`,
      icon: <XCircleIcon className="w-5 h-5" />,
      color: 'danger' as const,
      showToday: false,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Withdraw</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage withdrawal requests and transactions.
          </p>
        </div>
        <Button size="sm" onClick={() => setIsAddOpen(true)}>
          <PlusIcon className="w-4 h-4 mr-1.5" />
          Add Request
        </Button>
      </div>

      {/* Stats — 5 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {statCards.map((stat, i) => (
          <StatsCard key={i} {...stat} />
        ))}
      </div>

      {/* Filters */}
      <Card padding="sm" className="mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by telegram id, account, bank..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as any);
              setPage(1);
            }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="paid">Paid</option>
            <option value="rejected">Rejected</option>
          </select>

          <button
            onClick={handleSearchSubmit}
            className="text-sm px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Search
          </button>

          {(searchTerm || statusFilter !== 'all') && (
            <button
              onClick={handleClearFilters}
              className="text-sm px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card padding="sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-sm text-red-500">
              {error instanceof Error ? error.message : 'Failed to load withdrawals'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
            >
              Retry
            </button>
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-gray-500">No withdrawals found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Bank</th>
                  <th className="px-3 py-2 font-medium">Account</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((w: WithdrawalListItem) => (
                  <tr key={w.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                    <td className="px-3 py-3">
                      <p className="text-sm font-medium text-gray-900">{w.userName}</p>
                      <p className="text-[10px] text-gray-500">{w.telegramId}</p>
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-900">
                      {w.currency} {w.amount.toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      {w.bankName}
                      <br />
                      <span className="text-[10px] text-gray-400">{w.bankShortCode}</span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      {w.accountName}
                      <br />
                      <span className="text-[10px] text-gray-400">{w.accountNumber}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium uppercase ${statusBadge(
                          w.status
                        )}`}
                      >
                        {w.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {new Date(w.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => setDetailId(w.id)}
                        className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors"
                        title="View"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-3 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.pages} · {pagination.total} total
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={page >= pagination.pages}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      <WithdrawalDetailModal
        isOpen={!!detailId}
        withdrawalId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={handleChanged}
      />
      <AddWithdrawalModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSuccess={handleChanged}
      />
    </div>
  );
};

export default Withdraw;